import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as i18next from 'i18next';
import { required } from 'vuelidate/lib/validators';
import { G2S } from 'core/g2s';
import * as auth from 'core/auth';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import { ProductModel } from 'core/flights';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component({
    validations: {
        couponModel: {
            couponCode: { required }
        }
    }
})
export class FlightsBookingPaymentComponent extends Vue {
    $auth: auth.AuthService; // Injected in G2S.init

    @Prop()
    gaParams: utils.GoogleAnalyticsParams;

    @Prop()
    gaAlreadySubmitted: string[];

    @Prop()
    v: any;

    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    flightBookingDetails: api.FlightBookingDetails;

    @Prop()
    currentUser: api.UserWithBonuses;

    @Prop()
    paymentButtonPrice: number;

    @Prop()
    searchId: string;

    productModel: ProductModel;
    couponModel: any = {
        couponCode: '',
        submitted: false
    }

    bonusAmountInputValue: number = 0;

    get availableBonuses() {
        return this.reservation.payment.calculateAvailableBonuses(this.reservation.getTotalPrice(), this.currentUser.bonuses);
    }

    get bonusAmountLabel() {
        return i18next.t('label_bonuses_used', <any>{ i: (<any>this.$options.filters).number(this.reservation.payment!.bonusAmount) });
    }

    get couponAppliedLabel() {
        return i18next.t('label_coupon_applied_long_mobile', <any>{
            pName: this.reservation.couponProduct ? 'на услугу ' + this.reservation.couponProduct.product.name : 'на весь заказ!',
            currency: G2S.config.currencySymbol,
            coupon: this.couponModel.couponCode.toUpperCase()
        });
    }

    get couponAppliedDiscountLabel() {
        return i18next.t('label_coupon_applied_discount', <any>{
            i: (<any>this.$options.filters).number(this.reservation.couponDiscountAmount),
            currency: G2S.config.currencySymbol
        });
    }

    get totalLabel() {
        return i18next.t(
            'label_price_total',
            <any>{
                i: (<any>this.$options.filters).price(
                    this.reservation.getTotalPrice(),
                    false,
                    this.reservation.selectedFlight?.currencyCode
                )
            });
    }
    get totalLabelRu() {
        return i18next.t(
            'label_price_total');
    }

    get fullPrice() {
        return this.reservation.getTotalPrice() + this.reservation.couponDiscountAmount;
    }

    get totalMoneywallLabel() {
        return i18next.t(
            'label_price_total',
            <any>{
                i: (<any>this.$options.filters).price(
                    this.reservation.getMoneywallTotalPrice()
                )
            }
        );
    }

    get totalPerMonthLabel() {
        return i18next.t('button_payment_type_in_parts') + ' ' +
            (<any>this.$options.filters).price(
                utils.Payment.calcMoneyWallPrice(
                    this.reservation.getTotalPrice(),
                    this.reservation.selectedFlight!.legs[0].segments[0].departureDate
                )
            ) + ' в месяц';
    }

    get totalBonusesLabel() {
        if (this.currentUser) {
            return i18next.t('label_bonuses_current_balance', <any>{ i: (<any>this.$options.filters).number(this.currentUser.bonuses.status.currentBalance) });
        }
        return i18next.t('label_bonuses_current_balance', <any>{ i: 0 });
    }

    get totalBonusesWithLeftLabel() {
        if (this.currentUser) {
            return i18next.t('label_bonuses_current_balance_with_left', <any>{
                i: (<any>this.$options.filters).number(this.currentUser.bonuses.status.currentBalance),
                j: (<any>this.$options.filters).number(this.currentUser.bonuses.status.leftToFirstUse)
            });
        }
        return i18next.t('label_bonuses_current_balance', <any>{ i: 0 });
    }

    get productsForPaymentStep() {
        return this.reservation.products.filter(({ product }) =>
            product.stepNumber === 3 && product.flightId == this.reservation.selectedFlightIdForProducts || product.stepNumber === 3 && product.flightId == null
        );
    }

    get stepThreeBrands(){
        return this.reservation.brandedProductKits && this.reservation.brandedProductKits.find((brands) =>
            brands.stepOnForm === 3 && brands.kitTarget !== 'AdditionalPopup'
        );
    }

    isMarkerOwner(markerOwner: string): boolean {
        return utils.Cookies.getMarkerOwner() === markerOwner;
    }

    mounted() {
        const g2scpn = utils.Cookies.g2scpnGet();

        if (g2scpn) {
            this.couponModel.couponCode = g2scpn;
        }

        setTimeout(()=> {
            if(this.couponModel.couponCode.length > 0 && !this.reservation.couponDiscountAmount) {
                this.onCouponSubmit();
                utils.Cookies.g2scpnDelete();
            }
        }, 500);
        
        const productsRocket = [...this.productsForPaymentStep.map((product) => product.product)];
        if (this.stepThreeBrands && this.stepThreeBrands.products.length) {
            this.stepThreeBrands.products.forEach((brand) => {
                const productByBrand = this.reservation.products.find((product) => brand.id && product.product.id === brand.id);
                if (productByBrand) {
                    productsRocket.push(productByBrand.product);
                }
            });
        }
        new api.RetailRocket().orderFormStepOpened({
            flightKey: api.RetailRocketCL.flightKey,
            stepNumber: 3,
            products: productsRocket,
            bookingSessionId: this.flightBookingDetails.bookingSessionId,
            email: this.reservation.contacts.email
        });
    }

    getCommissionLabel(v: number): string {
        return i18next.t('label_payment_commission_withvalue', <any>{ i: (<any>this.$options.filters).price(v) });
    }

    onBonusAmountSubmit() {
        if (this.bonusAmountInputValue > 0) {
            const availableBonuses = this.availableBonuses;

            if (this.bonusAmountInputValue > availableBonuses) {
                (<any>this.$root.$refs).messageDialog.show({
                    message: i18next.t('error_bonuses_payment_limit', <any>{
                        i: (<any>this.$options.filters).number(availableBonuses)
                    }),
                    onOkCallback: () => {
                        this.bonusAmountInputValue = availableBonuses;
                    }
                });
            } else {
                this.reservation.payment!.bonusAmount = this.bonusAmountInputValue;

                (<any>this.$refs).modalBonuses.hide();
            }
        } else {
            this.reservation.payment!.bonusAmount = 0;

            (<any>this.$refs).modalBonuses.hide();
        }
    }

    onBonuses() {
        this.bonusAmountInputValue = 0;

        if (this.reservation.payment) {
            this.bonusAmountInputValue = this.reservation.payment.bonusAmount;
        }

        (<any>this.$refs).modalBonuses.show();
    }

    onCoupon() {
        (<any>this.$refs).modalCoupon.show();

        this.$nextTick(function() {
            (<any>this.$refs).couponCode.focus();
        });
    }

    onCouponSubmit() {
        (<any>this).$ga.sendEvent('flight.booking', 'coupon.submit', 'flight.booking.coupon.submit', this.gaParams);

        if (this.couponModel.couponCode) {
            this.$v.couponModel!.$touch();

            if (!this.$v.couponModel!.$error) {
                this.couponModel.submitted = true;

                const request: api.CouponCheckRequest = {
                    email: this.reservation.contacts.email,
                    partnerCookie: utils.Cookies.getPartnerCookie(),
                    code: utils.String.normalizeCoupon(this.couponModel.couponCode),
                    searchId: this.searchId,
                    flightId: this.reservation.selectedFlightId,
                    passengers: new api.ReservationService().createPassengerCreateData(this.reservation)
                };

                new api.CouponService().check(request).then(
                    (result: api.CouponCheckModel) => {
                        this.couponModel.submitted = false;
                        this.reservation.couponProductAdd = result.product;
                        this.reservation.couponId = result.id;
                        this.reservation.couponGuid = result.guid;
                        this.reservation.couponDiscountAmount = result.discountAmount;

                        this.gaParams.dimension9 = this.reservation.couponId.toString();

                        this.reservation.couponProductId = result.productId;
                        this.reservation.couponDiscountAmountOld = result.discountAmount;

                        const res =  this.reservation.products.find(item => item.product.id == result.productId);
                        if (res) {
                            res.selected = true;
                        }
                        this.reservation.couponDataActivate = new Date();
                        this.reservation.couponDataExpiring = result.expiryDate;
                        this.reservation.products ? this.reservation.couponProduct = this.reservation.products.find(item => item.product.id == result.productId) : null;

                        if (this.reservation.couponProduct) {
                            this.reservation.couponProduct.choiceType = api.ProductChoiceType.manualAccepted;
                        }

                        if (this.reservation.couponProductId !== null && this.reservation.couponProduct == null) {
                            this.productModel = new flights.ProductModel(this.reservation.couponProductAdd);
                            this.productModel.selected = true;
                            this.productModel.choiceType = api.ProductChoiceType.manualAccepted;
                            this.productModel.favorite = false;

                            localStorage.productCouponId = JSON.stringify(this.reservation.couponProductAdd.id);
                            this.reservation.rawProducts.push(this.productModel);
                            this.reservation.couponProduct = this.productModel;
                            (<any>this.$refs).modalCoupon.hide();
                        } else {
                            (<any>this.$refs).modalCoupon.hide();
                            (<any>this).$ga.sendEvent('flight.booking', 'coupon.applied', 'flight.booking.coupon.applied', this.gaParams);
                        }
                    },
                    (error) => {
                        this.couponModel.submitted = false;

                        (<any>this.$root.$refs).messageDialog.show({
                            title: i18next.t('error'),
                            message: error?.response?.data?.localizedMessage
                                ? error.response.data.localizedMessage
                                : i18next.t('error_server_tryagain')
                        });

                        (<any>this).$ga.sendEvent('flight.booking', 'coupon.failed', 'flight.booking.coupon.failed', this.gaParams);
                    }
                );
            }
        } else {
            // Delete coupon
            this.reservation.couponDiscountAmount = 0;
            this.reservation.couponId = null;
            this.reservation.couponGuid = null;
            this.gaParams.dimension9 = undefined;

            const res = this.reservation.products.find(item => {
                return item.product == this.reservation.couponProductAdd;
            });
            if (res) {
                res.selected = false;
            }
        }
    }

    onCardNumberInputOnce() {
        (<any>this).$ga.sendEvent('flights.booking', 'card.input.started', 'flights.booking.card.input.started', this.gaParams);
        (<any>this).$ga.sendPageview('/flights/booking/payment/card/inputstart', 'Flights / Booking / Payment / Card / Inputstart', this.gaParams);
    }

    onGoBack() {
        this.$emit('back');
    }

    onSubmit() {
        this.v.contacts.$touch();
        const paymentFormError = (<any>this.$refs).paymentForm.validate();

        const g = utils.clone(this.gaParams);
        g.dimension1 = this.reservation.payment!.selectedPaymentTypeId;
        const selectedPayment = this.reservation.payment!.selectedPaymentType;
        const selectedPaymentMethod = selectedPayment.isYandexPay ? this.reservation.payment!.selectedPaymentMethod : '';

        (<any>this).$ga.sendPageview('/flights/booking/submit', 'Flights / Booking / Submit', this.gaParams);

        (<any>this).$ga.sendEvent(
            'flights.booking',
            `payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.submit`.toLowerCase(),
            `flights.booking.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.submit`.toLowerCase(),
            this.gaParams
        );

        if (this.v.contacts.$error
            || paymentFormError) {
            let title: any = i18next.t('error');
            let error = null;

            if (!error
                && paymentFormError
                && paymentFormError !== i18next.t('error_fields')) {
                error = paymentFormError;
            }

            if (!error) {
                title = null; // Hide title
                error = i18next.t('error_fields');
            }

            (<any>this.$root.$refs).messageDialog.show({
                title: title,
                message: error
            });

            if (this.gaAlreadySubmitted.indexOf('flights.booking.step3.error') < 0) {
                (<any>this).$ga.sendEvent('flights.booking', 'step3.error', 'flights.booking.step3.error', this.gaParams);
                (<any>this).$ga.reachGoal('flights.booking.step3.error');

                this.gaAlreadySubmitted.push('flights.booking.step3.error');
            }
        } else {
            this.$emit('submit');
        }
    }

    onPrice() {
        (<any>this).$ga.sendEvent('flights.booking', 'btn.step3.details.price', 'flights.booking.btn.step3.details.price', this.gaParams);
        (<any>this.$refs).modalPrice.show();
    }

    onTerms() {
        (<any>this).$ga.sendEvent('flights.booking', 'flight.terms', 'flights.booking.flight.terms', this.gaParams);
        (<any>this.$refs).modalTerms.show();
    }

    onTermsAccept() {
        (<any>this.$refs).modalTerms.hide();
    }

    onTermsContent(content: string, title: string) {
        (<any>this.$refs).textContent.show(content, title);
    }

    onProductDetails(product: flights.ProductModel) {
        this.$emit('product-details', product);
    }

    onProductSelectionChange(product: flights.ProductModel, value: boolean) {
        this.$emit('product-selection-change', product, value);
    }

    onFlightTerms() {
        if (this.reservation.selectedFlight) {
            this.$emit('flight-terms', this.reservation.selectedFlight.uniqueId);
        }
    }
}
